import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Booking, Vehicle } from '@limo/database'

import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TablePagination } from '@/components/ui/table-pagination'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { MoreHorizontal, Eye, XCircle, ArrowLeftRight } from 'lucide-react'
import {
  deriveZapierSyncStatus,
  zapierStatusTooltip,
  ZAPIER_STATUS_BADGES,
  type ZapierSyncStatusFields,
} from '@/components/integrations/zapierSyncStatus'
import {
  BookingsFilterBar,
  ColumnPicker,
  ExportButton,
  SavedViewsMenu,
  SortableHeader,
} from '@/components/reporting'
import { useUrlBookingFilters } from '@/hooks/useUrlReportFilters'
import { useAccountTimezone, useBookingsReport } from '@/hooks/useReports'
import { useExportColumns } from '@/hooks/useExportColumns'
import {
  parseBookingFilters,
  type BookingFilters,
  type BookingSortField,
  type SavedView,
} from '@limo/shared'

// Extended booking type with vehicle relation
type BookingWithVehicle = Booking & {
  vehicles: Pick<Vehicle, 'name'> | null
  balance_due: number | null
}

// Status badge variants
const bookingStatusStyles: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending: { variant: 'secondary', label: 'Pending' },
  confirmed: { variant: 'default', label: 'Confirmed' },
  in_progress: { variant: 'default', label: 'In Progress' },
  completed: { variant: 'outline', label: 'Completed' },
  cancelled: { variant: 'destructive', label: 'Cancelled' },
  no_show: { variant: 'destructive', label: 'No Show' },
}

const paymentStatusStyles: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending: { variant: 'secondary', label: 'Pending' },
  paid: { variant: 'default', label: 'Paid' },
  refunded: { variant: 'outline', label: 'Refunded' },
  failed: { variant: 'destructive', label: 'Failed' },
}

export function BookingsPage() {
  const navigate = useNavigate()
  const { account, isAdmin } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const [cancelId, setCancelId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Use impersonated account when admin is impersonating, otherwise use real account
  const activeAccount = isImpersonating ? impersonatedAccount : account
  const timezone = useAccountTimezone()

  // Build base path for navigation (supports impersonation)
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Filters, sort and paging all live in the URL, so the view survives a refresh
  // and can be shared by pasting the link. Saved views work by writing to the
  // same place, which keeps one source of truth.
  const {
    filters,
    sort,
    page,
    pageSize,
    setFilters,
    setSort,
    setPage,
    setPageSize,
    clearFilters,
    applyView,
  } = useUrlBookingFilters()

  // Export column selection — affects the CSV download only, not the table
  // columns below. Kept out of the URL (it doesn't change which rows are
  // queried) but persisted per device so it survives a reload.
  const [selectedColumns, setSelectedColumns] = useExportColumns('bookings')

  // Server-side: filtered, sorted, paged, with an exact total. Replaces fetching
  // every row and filtering in JS, which silently truncated at max_rows = 1000.
  const { data, isLoading, isError, error } = useBookingsReport(filters, sort, page, pageSize)

  const bookings = (data?.rows ?? []) as unknown as BookingWithVehicle[]
  const totalCount = data?.total ?? 0

  const handleApplyView = (view: SavedView): void => {
    // Re-parse: the stored JSONB may predate the current filter shape.
    applyView(
      parseBookingFilters(view.filters as Record<string, unknown>),
      view.sort as { field: BookingSortField; direction: 'asc' | 'desc' } | null
    )
    if (view.columns.length > 0) setSelectedColumns(view.columns)
  }

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings-report'] })
      toast({
        title: 'Booking cancelled',
        description: 'The booking has been cancelled.',
      })
      setCancelId(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Per-account feature flag (Settings > Integrations > Zapier > LA
  // Reservation Confirmation): the Zapier column only exists for accounts
  // with the confirmation loop enabled.
  const { data: laFlag } = useQuery<boolean>({
    queryKey: ['zapier-la-confirmation-flag', activeAccount?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('value')
        .eq('account_id', activeAccount!.id)
        .eq('key', 'zapier_la_confirmation_enabled')
        .maybeSingle()

      if (error) throw error
      return data?.value === 'true'
    },
    enabled: isAdmin && !!activeAccount?.id,
  })
  const showZapierColumn = isAdmin && laFlag === true

  // Table geometry: the conditional Zapier column shifts skeleton/empty-state
  // spans, so derive the count instead of hardcoding it in three places.
  const columnCount = showZapierColumn ? 10 : 9

  // Format currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)

  // Format date in business timezone
  const formatDate = (dateStr: string, useTimezone = false) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(useTimezone && activeAccount?.timezone ? { timeZone: activeAccount.timezone } : {}),
    })

  // Format time (e.g. "2:30 PM"), optionally in the business timezone
  const formatTime = (dateStr: string, useTimezone = false) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...(useTimezone && activeAccount?.timezone ? { timeZone: activeAccount.timezone } : {}),
    })

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bookings</h1>
          <p className="text-muted-foreground">
            View, filter and export customer reservations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SavedViewsMenu
            resource="bookings"
            currentFilters={filters as Readonly<Record<string, unknown>>}
            currentSort={sort}
            currentColumns={selectedColumns}
            onApply={handleApplyView}
          />
          <ColumnPicker
            resource="bookings"
            selected={selectedColumns}
            onChange={setSelectedColumns}
          />
          <ExportButton
            resource="bookings"
            filters={filters}
            sort={sort}
            selectedColumns={selectedColumns}
            totalRows={totalCount}
          />
        </div>
      </div>

      {/* Filters */}
      <BookingsFilterBar
        filters={filters}
        timezone={timezone}
        onChange={(next: BookingFilters) => setFilters(next)}
        onClearAll={clearFilters}
      />

      {/* Bookings table */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle>All Bookings</CardTitle>
          <CardDescription>
            Customer reservations and their current status.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {/* Announce result-count changes for screen reader users. */}
          <p className="sr-only" aria-live="polite">
            {isLoading ? 'Loading bookings' : `${totalCount} bookings match the current filters`}
          </p>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Confirmation</TableHead>
                <SortableHeader
                  field="customer_name"
                  label="Customer"
                  sort={sort}
                  onSort={setSort}
                />
                <SortableHeader
                  field="pickup_datetime"
                  label="Date"
                  sort={sort}
                  onSort={setSort}
                />
                <SortableHeader field="created_at" label="Created" sort={sort} onSort={setSort} />
                <TableHead>Vehicle</TableHead>
                <SortableHeader field="total_amount" label="Total" sort={sort} onSort={setSort} />
                <SortableHeader
                  field="booking_status"
                  label="Status"
                  sort={sort}
                  onSort={setSort}
                />
                <SortableHeader
                  field="payment_status"
                  label="Payment"
                  sort={sort}
                  onSort={setSort}
                />
                {showZapierColumn && <TableHead>Zapier</TableHead>}
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: columnCount }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="h-24 text-center text-destructive">
                    Could not load bookings: {error instanceof Error ? error.message : 'Unknown error'}
                  </TableCell>
                </TableRow>
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="h-24 text-center">
                    No bookings match your filters. Clear them to see all reservations.
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => {
                  // Money still owed on this booking. Driven by the indexed
                  // `balance_due` generated column rather than a client-side
                  // sum, so the tint, the "has balance due" filter and the CSV
                  // export can never disagree about which rows are outstanding.
                  const balanceDue = booking.balance_due ?? 0
                  const amountPaid = booking.amount_paid ?? 0
                  // The tint means "this customer PART-paid and still owes the
                  // rest" — a deposit taken, balance outstanding. A booking
                  // nobody has paid a penny towards is not a collection problem,
                  // it's an unconfirmed booking, and tinting those made the list
                  // read as though every pending reservation were overdue.
                  const owesBalance = amountPaid > 0 && balanceDue > 0

                  return (
                  <TableRow
                    key={booking.id}
                    // Colour alone can't carry the meaning — the amount owed is
                    // also printed in the Total cell below, and the row is
                    // labelled for screen readers.
                    className={cn(
                      owesBalance && 'bg-red-50/60 hover:bg-red-50 border-l-2 border-l-red-400'
                    )}
                    aria-label={
                      owesBalance
                        ? `Booking ${booking.confirmation_code}, balance outstanding`
                        : undefined
                    }
                  >
                    <TableCell
                      className="cursor-pointer font-mono text-sm font-medium hover:underline"
                      onClick={() => navigate(`${basePath}/bookings/${booking.id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{booking.confirmation_code}</span>
                        {/* Paired-round-trip indicator. Clicking the badge
                            deep-links to the sibling leg's detail page so
                            dispatchers can hop between outbound and return in
                            two clicks. `trip_leg` is authoritative — legacy
                            single-row round trips (pairedBookingId null) show
                            no badge and keep rendering as today. */}
                        {booking.paired_booking_id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`${basePath}/bookings/${booking.paired_booking_id}`)
                            }}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                            title="View the paired leg"
                          >
                            <ArrowLeftRight className="h-3 w-3" />
                            {booking.trip_leg === 'outbound'
                              ? 'Leg 1 of 2'
                              : booking.trip_leg === 'return'
                                ? 'Leg 2 of 2'
                                : 'Paired'}
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className="cursor-pointer"
                      onClick={() => navigate(`${basePath}/bookings/${booking.id}`)}
                    >
                      <div>
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.customer_email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div>{formatDate(booking.pickup_datetime, true)}</div>
                      <div className="text-xs">{formatTime(booking.pickup_datetime, true)}</div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {booking.created_at ? (
                        <div>
                          <div>{formatDate(booking.created_at)}</div>
                          <div className="text-xs">{formatTime(booking.created_at)}</div>
                        </div>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{booking.vehicles?.name || '—'}</TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(booking.total_amount)}
                      {/* Surfaced inline because "has balance due" is a filter —
                          the operator should see why a row matched. */}
                      {owesBalance && (
                        <span className="block text-xs font-normal text-amber-700">
                          {formatCurrency(balanceDue)} due
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          bookingStatusStyles[booking.booking_status]?.variant ||
                          'secondary'
                        }
                      >
                        {bookingStatusStyles[booking.booking_status]?.label ||
                          booking.booking_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          paymentStatusStyles[booking.payment_status]?.variant ||
                          'secondary'
                        }
                      >
                        {paymentStatusStyles[booking.payment_status]?.label ||
                          booking.payment_status}
                      </Badge>
                    </TableCell>
                    {showZapierColumn && (
                      <TableCell>
                        <ZapierStatusBadge
                          booking={booking as unknown as ZapierSyncStatusFields}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`${basePath}/bookings/${booking.id}`)}
                            className="cursor-pointer"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {booking.booking_status !== 'cancelled' &&
                            booking.booking_status !== 'completed' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setCancelId(booking.id)}
                                  className="text-destructive cursor-pointer"
                                >
                                  <XCircle className="mr-2 h-4 w-4" />
                                  Cancel Booking
                                </DropdownMenuItem>
                              </>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          </div>
          {!isLoading && (
            // `count` is now the exact server total, so this reports the real
            // number of matching rows rather than however many were fetched.
            <TablePagination
              page={page}
              pageSize={pageSize}
              count={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </CardContent>
      </Card>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the booking as cancelled. If payment has been
              processed, you may need to issue a refund through your payment
              processor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelId && cancelMutation.mutate(cancelId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  )
}

/**
 * Where a booking sits in the Zapier → Limo Anywhere sync lifecycle
 * (scheduled / send failed / awaiting confirmation / confirmed / unconfirmed).
 * Admin-only ops signal — the mount site gates on isAdmin. Bookings with no
 * Zapier activity (integration disabled, legacy) render an em dash.
 */
function ZapierStatusBadge({ booking }: { booking: ZapierSyncStatusFields }) {
  const status = deriveZapierSyncStatus(booking)
  if (status === 'none') {
    return <span className="text-muted-foreground">—</span>
  }

  const config = ZAPIER_STATUS_BADGES[status]
  const label =
    status === 'confirmed' && booking.zapier_la_reservation_id
      ? `LA #${booking.zapier_la_reservation_id}`
      : config.label

  return (
    <Badge
      variant="outline"
      className={`whitespace-nowrap ${config.className}`}
      title={zapierStatusTooltip(status, booking) ?? undefined}
    >
      {label}
    </Badge>
  )
}
